import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Modal, Row, Space, Table, Typography } from "antd";
import IntlMessage from "../../components/IntlMessage";
import Currency from "../../components/Currency";
import useTime from "../../hooks/useTime";
import rateHistoriesApi from "../../api/rate-histories";
import FilterBar from "../../components/FilterBar";
import _, { debounce } from "lodash";
import dayjs from "dayjs";

const { Text } = Typography;

const History = (props) => {
  const { open, onClose, segment_id } = props;
  const isMount = useRef(false);
  const [reload, setReload] = useState(true);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    keyword: "",
    order_by: "id",
    order_dir: "DESC",
    // filter_name: ['segment_id'],
    // filter_value: [segment_id],
  });
  const [condition, setCondition] = useState({});
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(1);
  const { convertToAnyTimezone } = useTime();

  useEffect(() => {
    isMount.current = true;
    (async () => {
      try {
        if (isMount) {
          setLoading(true);
          const res = await rateHistoriesApi.getAll(filter);
          setItems(res.data);
          if (total !== items.total) {
            setTotal(items.total);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error("error", error);
        setLoading(false);
      }
    })();

    return () => {
      isMount.current = false;
    };
  }, [reload, filter]);

  useEffect(() => {
    if (filter?.filter_name && filter?.filter_value) {
      const newCondition = _.zipObjectDeep(
        filter?.filter_name,
        filter?.filter_value
      );
      setCondition(newCondition);
    }
  }, [filter]);

  const onReload = () => {
    setReload((reload) => !reload);
  };

  const handleCancel = () => {
    onClose();
  };

  const onChangeTable = async (
    pagination,
    filters,
    sorter,
    extra = { currentDataSource: [] }
  ) => {
    setFilter({
      ...filter,
      page: pagination.current,
      limit: pagination.pageSize,
      order_dir: getOrder(sorter.order),
      order_by: sorter.columnKey ? `${sorter.columnKey}` : "id",
    });
  };

  const getOrder = (order) => {
    if (order === "ascend") return "ASC";
    if (order === "descend") return "DESC";
    return "DESC";
  };

  const onFilter = debounce(async (value, title, type) => {
    if (type === "search") {
      setFilter((filter) => {
        return { ...filter, keyword: value, page: 1 };
      });
    } else {
      const filter_obj = { ...condition, [title]: value };
      let filter_name = [];
      let filter_value = [];
      for (var key in filter_obj) {
        if (filter_obj[key]) {
          filter_name.push(key);
          filter_value.push(filter_obj[key]);
        }
      }
      setFilter((filter) => {
        return {
          ...filter,
          filter_name: filter_name,
          filter_value: filter_value,
        };
      });
    }
  }, 300);

  const columns = [
    {
      title: <IntlMessage id="global.no" />,
      width: 20,
      render: (_, record, index) => {
        return index + 1 + filter.limit * (filter.page - 1);
      },
    },
    {
      title: <IntlMessage id="global.created_at" />,
      key: "created_at",
      dataIndex: "created_at",
      render: (_, record) => {
        return (
          <Text>{dayjs(record.created_at).format("DD/MM/YYYY HH:mm")}</Text>
        );
      },
    },
    {
      title: <IntlMessage id="global.segment" />,
      key: "segment",
      dataIndex: "segment",
      render: (_, record) => {
        return (
          <Text>
            {record?.segment?.start_stop?.title} -{" "}
            {record?.segment?.end_stop?.title}
          </Text>
        );
      },
    },
    // {
    //   title: <IntlMessage id="global.cabin" />,
    //   key: 'cabin',
    //   dataIndex: 'cabin',
    //   render: (_, record) => {
    //     return <Text>{record?.cabin?.title || record.cabin_title}</Text>;
    //   },
    // },
    {
      title: <IntlMessage id="global.price" />,
      key: "price",
      dataIndex: "price",
      render: (_, record) => {
        if (record?.params) {
          const convertAction = JSON.parse(record.params)?.action;

          if (convertAction === "delete") {
            return <Text></Text>;
          }
        }
        return <Currency value={record.price} />;
      },
    },
    {
      title: <IntlMessage id="global.action" />,
      key: "params",
      dataIndex: "price",
      render: (_, record) => {
        let actionText = "";
        if (record?.params) {
          const convertAction = JSON.parse(record.params)?.action;
          actionText = convertAction === "delete" ? "Remove" : "Create";
        }
        return <Text>{actionText}</Text>;
      },
    },
    {
      title: <IntlMessage id="history.from_date" />,
      key: "from_date",
      dataIndex: "from_date",
      render: (_, record) => {
        return (
          <Text>
            {convertToAnyTimezone(record.from_date).format("DD/MM/YYYY")}
          </Text>
        );
      },
    },
    {
      title: <IntlMessage id="history.to_date" />,
      key: "to_date",
      dataIndex: "to_date",
      render: (_, record) => {
        return (
          <Text>
            {convertToAnyTimezone(record.to_date).format("DD/MM/YYYY")}
          </Text>
        );
      },
    },
    {
      title: <IntlMessage id="history.week_days" />,
      key: "week_days",
      dataIndex: "week_days",
      render: (_, record) => {
        if (record?.params) {
          const convertAction = JSON.parse(record.params)?.action;

          if (convertAction === "delete") {
            return <Text></Text>;
          }
        }
        return <Text>{record.week_days}</Text>;
      },
    },
  ];

  return (
    <Modal
      title={<IntlMessage id="global.history" />}
      open={open}
      destroyOnClose={true}
      onCancel={handleCancel}
      width="60%"
      footer={[
        <Button key="back" type="default" onClick={handleCancel}>
          {<IntlMessage id="global.cancel" />}
        </Button>,
      ]}
    >
      <Space style={{ width: "100%" }} direction="vertical" size={4}>
        <Row justify={"space-between"}>
          <Col flex={1}>
            <FilterBar onFilter={onFilter} />
          </Col>
          <Col>
            <Button size="small" onClick={() => onReload()}>
              <IntlMessage id="global.reload" />
            </Button>
          </Col>
        </Row>
        <Table
          tableLayout="auto"
          columns={columns}
          dataSource={items}
          onChange={onChangeTable}
          rowKey="id"
          size="small"
          loading={loading}
          scroll={{
            x: "max-content",
          }}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            total: total,
            current: filter.page,
            pageSize: filter.limit,
          }}
        />
      </Space>
    </Modal>
  );
};

export default History;
